import { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Folder, File, Trash2, RefreshCw, Copy, AlertTriangle, HardDrive, CheckSquare, XSquare, ListChecks } from "lucide-react";

interface BucketInfo {
  bucket_id: string;
  label: string;
  allow_delete: boolean;
  is_enabled: boolean;
  danger_zone: boolean;
}

interface FolderItem {
  name: string;
  prefix: string;
}

interface FileItem {
  name: string;
  path: string;
  size: number | null;
  lastModified: string | null;
  mimeType: string | null;
}

interface DeletePreview {
  bucket_id: string;
  object_count: number;
  total_bytes: number | null;
  sample_paths: string[];
  warnings: string[];
  danger_zone: boolean;
}

type SelectionMode = "MANUAL" | "ALL_MATCHING";

interface AllMatchingQuery {
  bucketId: string;
  prefix: string;
  count: number;
  bytes: number | null;
  samplePaths: string[];
  warnings: string[];
}

const PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID;

async function invokeStorageManager(endpoint: string, options?: { method?: string; body?: any; params?: Record<string, string> }) {
  const session = (await supabase.auth.getSession()).data.session;
  if (!session) throw new Error("Not authenticated");

  const url = new URL(`https://${PROJECT_ID}.supabase.co/functions/v1/admin-storage-manager/${endpoint}`);
  if (options?.params) {
    for (const [k, v] of Object.entries(options.params)) {
      url.searchParams.set(k, v);
    }
  }

  const res = await fetch(url.toString(), {
    method: options?.method || "GET",
    headers: {
      "Authorization": `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
      "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    },
    body: options?.body ? JSON.stringify(options.body) : undefined,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status}: ${text.slice(0, 300)}`);
  }
  return res.json();
}

function formatBytes(bytes: number | null) {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export default function StorageManager() {
  const [selectedBucket, setSelectedBucket] = useState<string>("");
  const [prefixStack, setPrefixStack] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState<SelectionMode>("MANUAL");
  const [allMatchingQuery, setAllMatchingQuery] = useState<AllMatchingQuery | null>(null);
  const [excludedPaths, setExcludedPaths] = useState<Set<string>>(new Set());
  const [allMatchingConfirmOpen, setAllMatchingConfirmOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deletePreview, setDeletePreview] = useState<DeletePreview | null>(null);
  const [deleteProgress, setDeleteProgress] = useState<{ deleting: boolean; deleted: number; total: number } | null>(null);
  const queryClient = useQueryClient();

  const currentPrefix = prefixStack.join("/");

  // Fetch allowlisted buckets
  const { data: bucketsData, isLoading: bucketsLoading } = useQuery({
    queryKey: ["storage-manager-buckets"],
    queryFn: () => invokeStorageManager("buckets"),
  });

  const buckets: BucketInfo[] = bucketsData?.buckets || [];
  const currentBucketInfo = buckets.find(b => b.bucket_id === selectedBucket);

  // Fetch folder contents
  const { data: listData, isLoading: listLoading, refetch: refetchList } = useQuery({
    queryKey: ["storage-manager-list", selectedBucket, currentPrefix],
    queryFn: () => invokeStorageManager("list", { params: { bucket_id: selectedBucket, prefix: currentPrefix } }),
    enabled: !!selectedBucket,
  });

  const folders: FolderItem[] = listData?.folders || [];
  const files: FileItem[] = listData?.files || [];

  // All visible row keys
  const allVisibleKeys = useMemo(() => {
    const keys: string[] = [];
    for (const f of folders) keys.push(`folder:${f.prefix}`);
    for (const f of files) keys.push(`file:${f.path}`);
    return keys;
  }, [folders, files]);

  const visibleCount = allVisibleKeys.length;

  // Selection counts
  const selectedCount = selectionMode === "ALL_MATCHING"
    ? (allMatchingQuery?.count ?? 0) - excludedPaths.size
    : selected.size;

  // Header checkbox state
  const headerChecked = selectionMode === "ALL_MATCHING"
    ? excludedPaths.size === 0
    : visibleCount > 0 && selected.size === visibleCount;
  const headerIndeterminate = selectionMode === "ALL_MATCHING"
    ? excludedPaths.size > 0 && excludedPaths.size < (allMatchingQuery?.count ?? 0)
    : selected.size > 0 && selected.size < visibleCount;

  const clearSelection = useCallback(() => {
    setSelected(new Set());
    setSelectionMode("MANUAL");
    setAllMatchingQuery(null);
    setExcludedPaths(new Set());
  }, []);

  // Preview delete mutation
  const previewMutation = useMutation({
    mutationFn: (args: { prefixes: string[]; paths: string[] }) =>
      invokeStorageManager("preview-delete", {
        method: "POST",
        body: { bucket_id: selectedBucket, ...args },
      }),
    onSuccess: (data) => {
      setDeletePreview(data);
      setDeleteModal(true);
    },
    onError: (err: Error) => toast.error(`Preview failed: ${err.message}`),
  });

  // Preview for "select all matching" — reuses preview-delete endpoint
  const allMatchingPreviewMutation = useMutation({
    mutationFn: () =>
      invokeStorageManager("preview-delete", {
        method: "POST",
        body: { bucket_id: selectedBucket, prefixes: [currentPrefix], paths: [] },
      }),
    onSuccess: (data: DeletePreview) => {
      setAllMatchingQuery({
        bucketId: selectedBucket,
        prefix: currentPrefix,
        count: data.object_count,
        bytes: data.total_bytes,
        samplePaths: data.sample_paths,
        warnings: data.warnings,
      });
      setAllMatchingConfirmOpen(true);
    },
    onError: (err: Error) => toast.error(`Failed to count matching objects: ${err.message}`),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (args: { prefixes: string[]; paths: string[]; confirm_text: string }) =>
      invokeStorageManager("delete", {
        method: "POST",
        body: { bucket_id: selectedBucket, ...args },
      }),
    onSuccess: (data) => {
      setDeleteProgress(null);
      setDeleteModal(false);
      setConfirmText("");
      setDeletePreview(null);
      clearSelection();
      queryClient.invalidateQueries({ queryKey: ["storage-manager-list"] });
      if (data.failed?.length > 0) {
        toast.warning(`Deleted ${data.deleted_count} objects, ${data.failed.length} failed`);
      } else {
        toast.success(`Successfully deleted ${data.deleted_count} objects`);
      }
    },
    onError: (err: Error) => {
      setDeleteProgress(null);
      toast.error(`Delete failed: ${err.message}`);
    },
  });

  const navigateToFolder = (folderName: string) => {
    setPrefixStack([...prefixStack, folderName]);
    clearSelection();
  };

  const navigateToBreadcrumb = (index: number) => {
    setPrefixStack(prefixStack.slice(0, index));
    clearSelection();
  };

  const toggleSelect = (key: string) => {
    if (selectionMode === "ALL_MATCHING") {
      // In ALL_MATCHING mode, toggling adds/removes from exclusions
      const next = new Set(excludedPaths);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      setExcludedPaths(next);
      return;
    }
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const handleSelectAllVisible = () => {
    setSelectionMode("MANUAL");
    setAllMatchingQuery(null);
    setExcludedPaths(new Set());
    setSelected(new Set(allVisibleKeys));
  };

  const handleHeaderCheckbox = (checked: boolean) => {
    if (selectionMode === "ALL_MATCHING") {
      if (!checked) {
        clearSelection();
      } else {
        setExcludedPaths(new Set());
      }
      return;
    }
    if (checked) {
      handleSelectAllVisible();
    } else {
      clearSelection();
    }
  };

  const handleSelectAllMatching = () => {
    if (!currentPrefix) {
      toast.error("Navigate into a folder first — root-level bulk select is not allowed.");
      return;
    }
    allMatchingPreviewMutation.mutate();
  };

  const confirmAllMatching = () => {
    setSelectionMode("ALL_MATCHING");
    setSelected(new Set()); // clear manual selection
    setExcludedPaths(new Set());
    setAllMatchingConfirmOpen(false);
    // Select all visible rows visually
    toast.success(`Selected all ${allMatchingQuery?.count?.toLocaleString()} matching objects`);
  };

  const isRowChecked = (key: string): boolean => {
    if (selectionMode === "ALL_MATCHING") {
      return !excludedPaths.has(key);
    }
    return selected.has(key);
  };

  const handleDeleteSelected = () => {
    if (selectionMode === "ALL_MATCHING") {
      // Use the prefix-based approach
      const prefix = allMatchingQuery?.prefix || currentPrefix;
      previewMutation.mutate({ prefixes: prefix ? [prefix] : [], paths: [] });
      return;
    }

    const selectedPrefixes: string[] = [];
    const selectedPaths: string[] = [];

    for (const key of selected) {
      if (key.startsWith("folder:")) {
        selectedPrefixes.push(key.replace("folder:", ""));
      } else if (key.startsWith("file:")) {
        selectedPaths.push(key.replace("file:", ""));
      }
    }

    previewMutation.mutate({ prefixes: selectedPrefixes, paths: selectedPaths });
  };

  const handleConfirmDelete = () => {
    if (selectionMode === "ALL_MATCHING") {
      const prefix = allMatchingQuery?.prefix || currentPrefix;
      setDeleteProgress({ deleting: true, deleted: 0, total: deletePreview?.object_count || 0 });
      deleteMutation.mutate({ prefixes: prefix ? [prefix] : [], paths: [], confirm_text: confirmText });
      return;
    }

    const selectedPrefixes: string[] = [];
    const selectedPaths: string[] = [];

    for (const key of selected) {
      if (key.startsWith("folder:")) {
        selectedPrefixes.push(key.replace("folder:", ""));
      } else if (key.startsWith("file:")) {
        selectedPaths.push(key.replace("file:", ""));
      }
    }

    setDeleteProgress({ deleting: true, deleted: 0, total: deletePreview?.object_count || 0 });
    deleteMutation.mutate({ prefixes: selectedPrefixes, paths: selectedPaths, confirm_text: confirmText });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const expectedConfirm = `DELETE ${selectedBucket}`;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <HardDrive className="h-6 w-6 text-muted-foreground" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Storage Manager</h1>
              <p className="text-sm text-muted-foreground">Browse and manage storage buckets</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedCount > 0 && currentBucketInfo?.allow_delete && (
              <Button variant="destructive" size="sm" onClick={handleDeleteSelected} disabled={previewMutation.isPending}>
                <Trash2 className="h-4 w-4 mr-1" />
                Delete Selected ({selectedCount.toLocaleString()})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => refetchList()} disabled={!selectedBucket}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Bucket Selector */}
        <div className="flex items-center gap-4">
          <Select
            value={selectedBucket}
            onValueChange={(v) => {
              setSelectedBucket(v);
              setPrefixStack([]);
              clearSelection();
            }}
          >
            <SelectTrigger className="w-[280px]">
              <SelectValue placeholder={bucketsLoading ? "Loading…" : "Select a bucket"} />
            </SelectTrigger>
            <SelectContent>
              {buckets.map((b) => (
                <SelectItem key={b.bucket_id} value={b.bucket_id}>
                  <span className="flex items-center gap-2">
                    {b.label || b.bucket_id}
                    {b.danger_zone && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">danger</Badge>}
                    {!b.allow_delete && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">read-only</Badge>}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Breadcrumb */}
        {selectedBucket && (
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink className="cursor-pointer" onClick={() => navigateToBreadcrumb(0)}>
                  {selectedBucket}
                </BreadcrumbLink>
              </BreadcrumbItem>
              {prefixStack.map((segment, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    {i === prefixStack.length - 1 ? (
                      <BreadcrumbPage>{segment}</BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink className="cursor-pointer" onClick={() => navigateToBreadcrumb(i + 1)}>
                        {segment}
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </span>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        )}

        {/* Bulk Selection Controls */}
        {selectedBucket && visibleCount > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleSelectAllVisible} className="gap-1.5 text-xs h-7">
              <CheckSquare className="h-3.5 w-3.5" />
              Select all visible ({visibleCount})
            </Button>
            {selectedCount > 0 && (
              <Button variant="outline" size="sm" onClick={clearSelection} className="gap-1.5 text-xs h-7">
                <XSquare className="h-3.5 w-3.5" />
                Deselect all
              </Button>
            )}
            {currentBucketInfo?.allow_delete && currentPrefix && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAllMatching}
                disabled={allMatchingPreviewMutation.isPending || !currentPrefix}
                className="gap-1.5 text-xs h-7"
              >
                <ListChecks className="h-3.5 w-3.5" />
                {allMatchingPreviewMutation.isPending
                  ? "Counting…"
                  : selectionMode === "ALL_MATCHING"
                    ? `All matching selected (${allMatchingQuery?.count?.toLocaleString()})`
                    : "Select all matching filter…"
                }
              </Button>
            )}
            {selectedCount > 0 && (
              <Badge variant="secondary" className="text-xs h-7 px-2.5">
                {selectedCount.toLocaleString()} selected
                {selectionMode === "ALL_MATCHING" && excludedPaths.size > 0 && (
                  <span className="text-muted-foreground ml-1">({excludedPaths.size} excluded)</span>
                )}
              </Badge>
            )}
          </div>
        )}

        {/* Table */}
        {selectedBucket && (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    {visibleCount > 0 && (
                      <Checkbox
                        checked={headerIndeterminate ? "indeterminate" : headerChecked}
                        onCheckedChange={(checked) => handleHeaderCheckbox(!!checked)}
                      />
                    )}
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-24">Type</TableHead>
                  <TableHead className="w-24">Size</TableHead>
                  <TableHead className="w-48">Last Modified</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listLoading && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">Loading…</TableCell>
                  </TableRow>
                )}
                {!listLoading && folders.length === 0 && files.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      {currentPrefix ? "Empty folder" : "Empty bucket"}
                    </TableCell>
                  </TableRow>
                )}
                {folders.map((f) => {
                  const key = `folder:${f.prefix}`;
                  return (
                    <TableRow key={key} className="cursor-pointer">
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isRowChecked(key)}
                          onCheckedChange={() => toggleSelect(key)}
                        />
                      </TableCell>
                      <TableCell onClick={() => navigateToFolder(f.name)}>
                        <span className="flex items-center gap-2 font-medium">
                          <Folder className="h-4 w-4 text-muted-foreground" />
                          {f.name}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">Folder</TableCell>
                      <TableCell className="text-muted-foreground">—</TableCell>
                      <TableCell className="text-muted-foreground">—</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyToClipboard(f.prefix)}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {files.map((f) => {
                  const key = `file:${f.path}`;
                  return (
                    <TableRow key={key}>
                      <TableCell>
                        <Checkbox
                          checked={isRowChecked(key)}
                          onCheckedChange={() => toggleSelect(key)}
                        />
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-2">
                          <File className="h-4 w-4 text-muted-foreground" />
                          {f.name}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">{f.mimeType || "File"}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{formatBytes(f.size)}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {f.lastModified ? new Date(f.lastModified).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyToClipboard(f.path)}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Select All Matching Confirmation Modal */}
        <Dialog open={allMatchingConfirmOpen} onOpenChange={setAllMatchingConfirmOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ListChecks className="h-5 w-5 text-primary" />
                Select All Matching Objects
              </DialogTitle>
              <DialogDescription>
                This will select all objects matching your current filter.
              </DialogDescription>
            </DialogHeader>
            {allMatchingQuery && (
              <div className="space-y-3">
                <div className="rounded-md bg-muted p-3 text-sm space-y-1">
                  <p><strong>Bucket:</strong> {allMatchingQuery.bucketId}</p>
                  <p><strong>Prefix:</strong> {allMatchingQuery.prefix || "(root)"}</p>
                  <p><strong>Objects:</strong> {allMatchingQuery.count.toLocaleString()}</p>
                  {allMatchingQuery.bytes !== null && (
                    <p><strong>Total size:</strong> {formatBytes(allMatchingQuery.bytes)}</p>
                  )}
                </div>
                {allMatchingQuery.warnings.length > 0 && (
                  <div className="rounded-md border border-warning bg-warning/10 p-3 text-sm space-y-1">
                    {allMatchingQuery.warnings.map((w, i) => (
                      <p key={i} className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                        {w}
                      </p>
                    ))}
                  </div>
                )}
                {allMatchingQuery.samplePaths.length > 0 && (
                  <div className="max-h-32 overflow-auto rounded-md bg-muted p-2 text-xs font-mono space-y-0.5">
                    {allMatchingQuery.samplePaths.slice(0, 10).map((p, i) => (
                      <div key={i} className="truncate">{p}</div>
                    ))}
                    {allMatchingQuery.count > 10 && (
                      <div className="text-muted-foreground pt-1">
                        …and {(allMatchingQuery.count - 10).toLocaleString()} more
                      </div>
                    )}
                  </div>
                )}
                {allMatchingQuery.count > 5000 && (
                  <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm">
                    <strong>⚠️ Large selection</strong> — {allMatchingQuery.count.toLocaleString()} objects. You can exclude individual items after confirming.
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setAllMatchingConfirmOpen(false)}>Cancel</Button>
              <Button onClick={confirmAllMatching}>
                Select All ({allMatchingQuery?.count?.toLocaleString()})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModal} onOpenChange={(open) => { if (!deleteMutation.isPending) setDeleteModal(open); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Confirm Deletion
              </DialogTitle>
              <DialogDescription>This action cannot be undone.</DialogDescription>
            </DialogHeader>

            {deletePreview && !deleteProgress && (
              <div className="space-y-4">
                <div className="rounded-md bg-muted p-3 text-sm space-y-1">
                  <p><strong>Bucket:</strong> {deletePreview.bucket_id}</p>
                  <p><strong>Objects to delete:</strong> {deletePreview.object_count.toLocaleString()}</p>
                  {deletePreview.total_bytes !== null && (
                    <p><strong>Total size:</strong> {formatBytes(deletePreview.total_bytes)}</p>
                  )}
                </div>

                {deletePreview.warnings.length > 0 && (
                  <div className="rounded-md border border-warning bg-warning/10 p-3 text-sm space-y-1">
                    {deletePreview.warnings.map((w, i) => (
                      <p key={i} className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                        {w}
                      </p>
                    ))}
                  </div>
                )}

                {deletePreview.danger_zone && (
                  <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm">
                    <strong>⚠️ Danger zone bucket.</strong> Verify you intend to delete from this protected bucket.
                  </div>
                )}

                {deletePreview.sample_paths.length > 0 && (
                  <div className="max-h-40 overflow-auto rounded-md bg-muted p-2 text-xs font-mono space-y-0.5">
                    {deletePreview.sample_paths.map((p, i) => (
                      <div key={i} className="truncate">{p}</div>
                    ))}
                    {deletePreview.object_count > deletePreview.sample_paths.length && (
                      <div className="text-muted-foreground pt-1">
                        …and {(deletePreview.object_count - deletePreview.sample_paths.length).toLocaleString()} more
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <p className="text-sm">
                    Type <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">{expectedConfirm}</code> to confirm:
                  </p>
                  <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={expectedConfirm}
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            )}

            {deleteProgress && (
              <div className="space-y-3 py-4">
                <p className="text-sm text-center">Deleting objects…</p>
                <Progress value={deleteProgress.total > 0 ? (deleteProgress.deleted / deleteProgress.total) * 100 : 0} />
                <p className="text-xs text-center text-muted-foreground">
                  {deleteProgress.deleted} / {deleteProgress.total}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal(false)} disabled={deleteMutation.isPending}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={confirmText !== expectedConfirm || deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
